import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { activateSnackbar } from 'store/features/ui';
import { useLazyCreateIssueTicketQuery } from 'store/services/issue-tickets';
import { FormModal } from 'components/form/FormModal/FormModal';
import { FieldValues } from 'react-hook-form';
import { FormSelectOption } from 'components/form/types';
import React, { useEffect, useState } from 'react';
import { RootState } from 'store';
import { AddIssueForm } from './AddIssueForm';
import { getNewPortalUsers } from 'lib/firebase';
import { retrievedNewPortalUsers } from 'store/features/authentication';
import { convertDeliveryStatusToInternalName } from 'lib/constants';
import { DeviceLookup } from 'views/Planning/Planning';
import { isEmpty } from 'lodash';

export interface AddIssueModalProps extends DefaultOpenModalProps {
    supplierId?: string;
    /** When ticket is added use set state callback to alert parent component to refetch tickets. */
    onAddIssue?: React.Dispatch<React.SetStateAction<boolean>>;
    devicesList: DeviceLookup;
    setDevicesList: React.Dispatch<React.SetStateAction<DeviceLookup>>;
}

type IssueTicketInput =
    | Omit<Partial<Ticket>, 'taggedUsers | taggedDevices'>
    | {
          taggedUsers?: string;
          taggedDevices?: string;
          selectedLedSequence?: string;
      };

const defaultValuesIssueTicket: IssueTicketInput = {
    name: '',
    description: '',
    taggedDevices: '',
    taggedUsers: '',
    status: convertDeliveryStatusToInternalName['In Progress'],
    ticketTrigger: '',
    selectedLedSequence: '',
};

export const AddIssueModal = ({
    modalOpen,
    setModalOpen,
    supplierId,
    onAddIssue,
    devicesList,
    setDevicesList,
}: AddIssueModalProps) => {
    const dispatch = useDispatch();
    const authentication = useSelector(
        (state: RootState) => state.authentication
    );
    const [userOptions, setUserOptions] = useState<FormSelectOption[]>([]);

    useEffect(() => {
        if (
            (supplierId || supplierId?.toString() === '0') &&
            isEmpty(authentication.newPortalUsers)
        ) {
            getNewPortalUsers(supplierId).then((newPortalUsers) => {
                if (newPortalUsers) {
                    dispatch(retrievedNewPortalUsers(newPortalUsers));
                    const userOpts = Object.keys(newPortalUsers).map(
                        (key: string) => {
                            return {
                                name: newPortalUsers[key]?.email || '',
                                value: newPortalUsers[key]?.uid || '',
                            };
                        }
                    );

                    setUserOptions(userOpts);
                }
            });
        }
    }, [supplierId, authentication.newPortalUsers, dispatch]);

    const [createIssueTicketQuery] = useLazyCreateIssueTicketQuery();

    const addIssueTicketSchema = yup.object({
        name: yup.string().required(),
        description: yup.string().required(),
        taggedDevices: yup.string().required(),
        taggedUsers: yup.string().required(),
        status: yup.string(),
        ticketTrigger: yup.string(),
        selectedLedSequence: yup.string().when('ticketTrigger', {
            is: (issueType: string) => issueType === 'offline',
            then: (schema) => schema.required(),
        }),
    });

    const onClose = () => {
        setModalOpen(false);
    };

    const onError = (error: FieldValues) => {
        const messages = Object.keys(error).map((key) => error[key]?.message);
        dispatch(
            activateSnackbar({
                message: JSON.stringify(messages),
                severity: 'error',
            })
        );
    };

    const onSubmit = (data: FieldValues) => {
        const {
            name,
            description,
            taggedDevices,
            taggedUsers,
            status,
            ticketTrigger,
            selectedLedSequence,
        } = data;

        const selectedDevice = devicesList[taggedDevices];

        const selectedUser = taggedUsers
            ? authentication.newPortalUsers?.[taggedUsers]
            : null;

        if (!selectedDevice) {
            return dispatch(
                activateSnackbar({
                    message: 'Device required.',
                    severity: 'error',
                })
            );
        }

        const newTicket: Partial<Ticket> = {
            createdBy: authentication.user?.uid ?? '',
            name,
            description,
            taggedDevices: [selectedDevice],
            status,
            ticketCategory: 'issue',
            ticketType: 'issue',
            ticketTrigger,
            offlineLedSequence: selectedLedSequence,
            supplierId,
            sleep: {
                status: false,
                days: '0',
            },
            deviceId: selectedDevice.deviceId,
        };

        if (selectedUser) newTicket.taggedUsers = [selectedUser];

        if (supplierId) {
            createIssueTicketQuery({
                supplierId,
                deviceId: selectedDevice.deviceId,
                ticket: newTicket,
            }).then(() => {
                if (onAddIssue) onAddIssue(true);
                onClose();
            });
        }
    };

    return (
        <FormModal
            validationSchema={addIssueTicketSchema}
            defaultValues={defaultValuesIssueTicket}
            title="Create Issue Ticket"
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            onValidHandler={onSubmit}
            onErrorHandler={onError}
            testId="AddIssueModal"
        >
            <AddIssueForm
                userOptions={userOptions}
                supplierId={supplierId}
                devicesList={devicesList}
                setDevicesList={setDevicesList}
            />
        </FormModal>
    );
};
